import React, { useState, useEffect, useRef } from 'react';
import { SearchIcon, ChevronDownIcon, CheckIcon } from './Icons';

export default function CurrencySelect({ value, onChange, currencies = [], disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Keep selected currency synchronized with value prop
  useEffect(() => {
    if (!value) return;
    const matched = currencies.find(c => c.code === value);
    if (matched) {
      setSelectedCurrency(matched);
    } else if (value && !selectedCurrency) {
      setSelectedCurrency({ code: value, name: value, symbol: '' });
    }
  }, [value, currencies]);

  // Click outside listener to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (currency) => {
    setSelectedCurrency(currency);
    if (onChange) onChange(currency);
    setIsOpen(false);
    setSearchQuery('');
  };

  const filteredCurrencies = currencies.filter(c => {
    const q = searchQuery.toLowerCase();
    return (
      c.code.toLowerCase().includes(q) ||
      (c.name && c.name.toLowerCase().includes(q)) ||
      (c.symbol && c.symbol.toLowerCase().includes(q))
    );
  });

  return (
    <div className="relative inline-block w-full text-left" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg border transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
      >
        <span className="flex items-center space-x-2 truncate">
          <span className="font-medium text-slate-800 dark:text-slate-200 truncate">
            {selectedCurrency ? `${selectedCurrency.code} ${selectedCurrency.symbol ? `(${selectedCurrency.symbol})` : ''} - ${selectedCurrency.name}` : 'Select Currency'}
          </span>
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1.5 w-72 max-h-72 z-50 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden flex flex-col">
          {/* Search Input */}
          <div className="p-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <div className="relative">
              <SearchIcon className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search currency..."
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                name="currency_search"
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto flex-1 divide-y divide-slate-100 dark:divide-slate-700/50">
            {filteredCurrencies.length === 0 ? (
              <div className="p-4 text-xs text-center text-slate-500 dark:text-slate-400">
                No matching currencies found
              </div>
            ) : (
              filteredCurrencies.map((currency) => {
                const isSelected = selectedCurrency?.code === currency.code;
                return (
                  <button
                    key={currency.code}
                    type="button"
                    onClick={() => handleSelect(currency)}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between transition-colors ${
                      isSelected
                        ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 font-medium'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700/40 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2 truncate">
                      <span className="truncate text-slate-900 dark:text-slate-100">
                        {currency.code} {currency.symbol ? `(${currency.symbol})` : ''} - {currency.name}
                      </span>
                    </div>
                    {isSelected && <CheckIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
